/**
 * The single typed API client. Only feature `api/` folders and shared/api may import it.
 * - typed by the OpenAPI contract (packages/api-schema)
 * - cookies (session) + CSRF header on mutating requests
 * - X-Request-Id on every request
 * - 401 pauses the request, opens re-authentication, then retries it once
 * - every failure becomes an ApiError via unwrap()
 */
import type { paths } from '@dealdesk/api-schema';
import createClient, { type Middleware } from 'openapi-fetch';

import { env } from '@/shared/config/env';
import { i18next } from '@/shared/i18n';

import { authGate } from './auth-gate';
import { ApiError, toApiError } from './errors';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
/** Sign-in steps: a 401 here is an answer (wrong code, expired ticket), never a reason to open re-authentication. */
const AUTH_PATHS = [
  '/auth/login',
  '/auth/mfa',
  '/auth/mfa/enrol',
  '/auth/mfa/enrol/confirm',
  '/auth/mfa/recovery',
  '/auth/password-reset/request',
  '/auth/password-reset/complete',
  '/auth/logout',
  '/auth/logout-all',
];
/** CSRF cookie names in order of preference: configured, then the production `__Host-` name, then plain-http dev. */
const CSRF_COOKIES = [env.csrfCookie, '__Host-dd_csrf', 'dd_csrf'].filter(Boolean);

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function csrfToken(): string | null {
  for (const name of CSRF_COOKIES) {
    const value = readCookie(name);
    if (value) return value;
  }
  return null;
}

const clones = new Map<string, Request>();

const middleware: Middleware = {
  onRequest({ request, id }) {
    request.headers.set('X-Request-Id', crypto.randomUUID());
    request.headers.set('Accept-Language', i18next.language || 'en');
    if (MUTATING.has(request.method)) {
      const csrf = csrfToken();
      if (csrf) request.headers.set('X-CSRF-Token', csrf);
    }
    clones.set(id, request.clone());
    return request;
  },
  async onResponse({ response, id, schemaPath }) {
    const original = clones.get(id);
    clones.delete(id);
    if (response.status !== 401 || AUTH_PATHS.includes(schemaPath) || !original || !authGate.isEnabled) return response;
    const ok = await authGate.waitForReauth();
    if (!ok) return response;
    return fetch(original);
  },
  onError({ id }) {
    clones.delete(id);
    return undefined;
  },
};

export const api = createClient<paths>({
  baseUrl: env.apiBaseUrl,
  credentials: 'include',
  querySerializer: { array: { style: 'form', explode: false } },
});
api.use(middleware);

type FetchResult<T> = { data?: T; error?: unknown; response: Response };

/** Returns the response body or throws an ApiError. Use in every queryFn / mutationFn. */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await promise;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw toApiError(e);
  }
  const { data, error, response } = result;
  if (!response.ok) throw toApiError(error, response);
  return data as T;
}

export { ApiError };
